// Copyright ©️ 2026 eVolve MEP, LLC

import { Flex, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { GridMenu } from 'components/Mantine/GridMenu';
import { StatusPoint } from 'components/Mantine/StatusPoint';

import { AddEditStatusPopover } from './AddEditStatusPopover';
import type { CustomStatus, CustomStatusEntityName } from './types';

type Props<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
  status: CustomStatus<TEntity>;
  onDeleteStatus: (status: CustomStatus<TEntity>) => void;
};

/** Single status row inside a `StateAccordionItem` — displays the status and an Edit/Delete menu. */
export const StatusRow = <TEntity extends CustomStatusEntityName>({
  customStatusTypeName,
  status,
  onDeleteStatus,
}: Props<TEntity>) => {
  const [editPopoverOpened, { open: openEditPopover, close: closeEditPopover }] = useDisclosure(false);

  return (
    <Flex align="center" justify="space-between" px="lg" py="xs">
      <Flex align="center" gap="sm">
        <StatusPoint color={status.color} tooltip={status.name} />
        <Text>{status.name}</Text>
      </Flex>
      <AddEditStatusPopover
        customStatusTypeName={customStatusTypeName}
        stateId={status.stateId}
        status={status}
        opened={editPopoverOpened}
        onClose={closeEditPopover}
        target={
          <GridMenu
            options={['Edit', 'Delete']}
            onMenuItemSelected={(selected) => {
              if (selected === 'Edit') openEditPopover();
              if (selected === 'Delete') onDeleteStatus(status);
            }}
          />
        }
      />
    </Flex>
  );
};
